import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Sparkle, Copy, ArrowsClockwise, FloppyDisk, CircleNotch, ChatCenteredDots, Lightning, MagicWand } from "@phosphor-icons/react";

const STANCE_META = {
  "add-value":        { label: "Add value",       hint: "Share a specific example, mini-framework, or lesson that extends the OP's insight." },
  "agree-and-extend": { label: "Agree + extend",  hint: "Agree with the specific insight, then push it further with nuance the OP missed." },
  "contrarian":       { label: "Contrarian",      hint: "Respectfully push back with a precise counter-example. Never dismissive." },
  "ask-question":     { label: "Ask a question",  hint: "Ask ONE precise, curiosity-driven question that unlocks the OP to say more." },
};
const ALL_STANCE_KEYS = Object.keys(STANCE_META);

/**
 * CommentSuggestModal
 * Paste an OP's LinkedIn post + your rough PoV → AI drafts a comment in the user's voice.
 * Trial cap: 5 suggestions (backend-enforced, separate from the 5 comment logs).
 */
export default function CommentSuggestModal({ open, onClose, onSavedAsComment }) {
  const [originalPost, setOriginalPost] = useState("");
  const [myPov, setMyPov] = useState("");
  const [stance, setStance] = useState("add-value");
  const [suggestion, setSuggestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingLog, setSavingLog] = useState(false);
  const [meta, setMeta] = useState(null); // {suggestions_used, suggestions_limit, plan}
  const [aiStances, setAiStances] = useState([]); // AI-recommended stances for THIS post
  const [analyzing, setAnalyzing] = useState(false);
  const analyzeSeq = useRef(0);

  const canGenerate = originalPost.trim().length >= 10 && !busy;

  // Auto-suggest best stances when the OP post is pasted / stable for 1.2s.
  useEffect(() => {
    const seq = ++analyzeSeq.current;
    setAiStances([]);
    if (originalPost.trim().length < 40) return;
    const t = setTimeout(async () => {
      setAnalyzing(true);
      try {
        const r = await api.post("/comments/stances", { original_post: originalPost.trim() });
        if (seq === analyzeSeq.current) {
          setAiStances(r.data.stances || []);
          // Auto-select the top-ranked suggestion so the user can hit Generate immediately
          const top = r.data.stances?.[0]?.key;
          if (top && ALL_STANCE_KEYS.includes(top)) setStance(top);
        }
      } catch (e) {
        // silent — fall back to manual stance chips
      } finally {
        if (seq === analyzeSeq.current) setAnalyzing(false);
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [originalPost]);

  const generate = async () => {
    if (!canGenerate) return;
    setBusy(true);
    setSuggestion("");
    try {
      const r = await api.post("/comments/suggest", {
        original_post: originalPost.trim(),
        my_pov:        myPov.trim(),
        stance,
      });
      setSuggestion(r.data.suggestion || "");
      setMeta({
        used:  r.data.suggestions_used,
        limit: r.data.suggestions_limit,
        plan:  r.data.plan,
      });
    } catch (e) {
      if (e?.response?.status === 402) toast.error(e.response.data.detail || "Trial limit reached.");
      else toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const copySuggestion = async () => {
    if (!suggestion.trim()) return;
    try {
      await navigator.clipboard.writeText(suggestion);
      toast.success("Copied. Paste it into LinkedIn.");
    } catch { toast.error("Clipboard blocked. Select and copy manually."); }
  };

  const saveAsCommentLog = async () => {
    if (!suggestion.trim()) return;
    setSavingLog(true);
    try {
      await api.post("/comments", {
        comment_text: suggestion,
        author_name:  "",
        post_url:     "",
        post_context: originalPost.slice(0, 500),
        reactions:    0,
        replies:      0,
        connections_from_it: 0,
        tags:         [],
      });
      toast.success("Saved to your Comments log.");
      onSavedAsComment?.();
      onClose();
    } catch (e) {
      if (e?.response?.status === 402) toast.error(e.response.data.detail || "Trial log limit reached.");
      else toast.error(formatApiError(e));
    } finally { setSavingLog(false); }
  };

  const close = () => {
    // do NOT reset the inputs so users can reopen and iterate; only reset on unmount via parent
    onClose();
  };

  if (!open) return null;
  const remaining = meta ? Math.max(0, (meta.limit || 0) - (meta.used || 0)) : null;
  const isTrial = meta?.plan === "trial";

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.55)" }}
      data-testid="comment-suggest-backdrop"
    >
      <div className="card w-full max-w-3xl p-0 fade-up flex flex-col" data-testid="comment-suggest-modal" style={{ maxHeight: "88vh" }}>
        <div className="flex items-start justify-between gap-4 p-6 pb-3 shrink-0">
          <div>
            <div className="eyebrow mb-1 flex items-center gap-1.5" style={{ color: "var(--accent-2)" }}>
              <Lightning size={12} weight="fill"/> Comment coach
            </div>
            <h3 className="font-serif text-2xl font-semibold">Draft a comment in your voice.</h3>
            <p className="text-sm mt-1" style={{ color: "var(--ink-3)" }}>
              Paste the post you want to react to and share your rough take. She'll shape it into a LinkedIn-ready comment that sounds like you.
            </p>
          </div>
          <button type="button" onClick={close} data-testid="comment-suggest-close" className="p-2 rounded-full hover:bg-[color:var(--paper)]" aria-label="Close"><X size={16}/></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto scroll-thin px-6 pb-4">
          <label className="block mb-3">
            <div className="eyebrow mb-1">The post you want to comment on</div>
            <textarea
              data-testid="comment-suggest-original"
              className="textarea"
              rows={5}
              placeholder="Paste the LinkedIn post here."
              value={originalPost}
              onChange={(e) => setOriginalPost(e.target.value)}
            />
          </label>

          <label className="block mb-3">
            <div className="eyebrow mb-1">Your take, PoV, or rough thought (optional but recommended)</div>
            <textarea
              data-testid="comment-suggest-pov"
              className="textarea"
              rows={3}
              placeholder="What do you want to say? Rough is fine. She'll preserve your facts and phrasing."
              value={myPov}
              onChange={(e) => setMyPov(e.target.value)}
            />
          </label>

          <div className="mb-3">
            <div className="eyebrow mb-2 flex items-center gap-1.5">
              Stance
              {analyzing && <span className="inline-flex items-center gap-1 text-[10px] font-mono normal-case" style={{ color: "var(--ink-3)" }}><CircleNotch size={10} className="animate-spin"/> analyzing post…</span>}
              {!analyzing && aiStances.length > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-mono normal-case" style={{ color: "var(--accent-2)" }}><MagicWand size={10} weight="fill"/> she picked 3 that would stand out</span>}
            </div>

            {aiStances.length > 0 && (
              <div className="space-y-2 mb-3" data-testid="stances-recommended">
                {aiStances.map((s, i) => {
                  const active = stance === s.key;
                  const meta = STANCE_META[s.key];
                  if (!meta) return null;
                  return (
                    <button
                      key={s.key + i}
                      onClick={() => setStance(s.key)}
                      data-testid={`stance-rec-${i}`}
                      data-active={active}
                      className="w-full text-left rounded-2xl p-3 transition"
                      style={active
                        ? { background: "var(--accent-tint)", border: "1px solid var(--accent)" }
                        : { background: "var(--bg-soft)", border: "1px solid var(--line-2)" }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-mono" style={{ color: active ? "var(--accent-2)" : "var(--ink-3)" }}>#{i + 1}</span>
                        <span className="text-sm font-medium" style={active ? { color: "var(--accent-2)" } : {}}>{s.label || meta.label}</span>
                        <span className="chip" style={{ padding: "1px 8px", fontSize: 10, background: active ? "var(--accent)" : "var(--bg-elev)", color: active ? "#fff" : "var(--ink-3)", borderColor: "transparent" }}>{meta.label}</span>
                      </div>
                      <div className="text-xs" style={{ color: "var(--ink-2)" }}>{s.why}</div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="text-[11px] mb-1.5" style={{ color: "var(--ink-3)" }}>Or pick manually:</div>
            <div className="flex flex-wrap gap-2" data-testid="comment-suggest-stances">
              {ALL_STANCE_KEYS.map((key) => (
                <button
                  key={key}
                  onClick={() => setStance(key)}
                  data-testid={`stance-${key}`}
                  data-active={stance === key}
                  className="chip"
                  style={stance === key ? { background: "var(--ink)", color: "#fff", borderColor: "var(--ink)" } : {}}
                  title={STANCE_META[key].hint}
                >
                  {STANCE_META[key].label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={generate}
              disabled={!canGenerate}
              data-testid="comment-suggest-generate"
              className="btn btn-accent"
            >
              {busy ? <><CircleNotch size={14} className="animate-spin"/> Drafting…</> : <><Sparkle size={14} weight="fill"/> {suggestion ? "Regenerate" : "Draft comment"}</>}
            </button>
            {meta && (
              <span
                className="text-xs font-mono"
                data-testid="comment-suggest-quota"
                style={{ color: isTrial && remaining <= 1 ? "#b83a2b" : "var(--ink-3)" }}
              >
                {isTrial ? `${meta.used}/${meta.limit} suggestions used` : "Pro · unlimited"}
              </span>
            )}
          </div>

          {suggestion && (
            <div className="card p-4 fade-up" data-testid="comment-suggest-result" style={{ background: "var(--bg-soft)", border: "1px solid var(--accent)" }}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="eyebrow flex items-center gap-1.5" style={{ color: "var(--accent-2)" }}>
                  <ChatCenteredDots size={12} weight="fill"/> Suggested comment
                </div>
                <div className="flex items-center gap-1.5">
                  <button data-testid="comment-suggest-copy" onClick={copySuggestion} className="btn btn-ghost text-xs py-1 px-2.5"><Copy size={12}/> Copy</button>
                  <button data-testid="comment-suggest-regen" onClick={generate} disabled={busy} className="btn btn-ghost text-xs py-1 px-2.5"><ArrowsClockwise size={12}/> Regenerate</button>
                  <button data-testid="comment-suggest-save-log" onClick={saveAsCommentLog} disabled={savingLog} className="btn btn-ghost text-xs py-1 px-2.5">
                    {savingLog ? <><CircleNotch size={12} className="animate-spin"/> Saving…</> : <><FloppyDisk size={12}/> Save to log</>}
                  </button>
                </div>
              </div>
              <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--ink)" }}>{suggestion}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
